import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";

import { AuthProvider } from "./lib/auth";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import BackToTop from "./components/BackToTop";
import CopyAttribution from "./components/CopyAttribution";
import PageViewTracker from "./lib/pageviewTracker";
import MetaPixelRouteTracker from "./components/MetaPixelRouteTracker";
import ChatWidget from "./components/chat/ChatWidget";
import { initGoogleAds } from "./lib/googleAds";

import { renderRoutes } from "./routes";

/** Shown for the few hundred ms a route chunk takes to arrive. */
export function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]" data-testid="route-loading">
      <p className="font-serif text-2xl text-[#787671]">Loading…</p>
    </div>
  );
}

/**
 * Wraps the app's <Routes /> in a RouteErrorBoundary keyed on `location.pathname`,
 * so any render error on a public OR authenticated page falls back to a friendly
 * message instead of a blank white screen. Navigating to a different route
 * remounts the boundary (fresh state) so a stuck error never follows the user.
 */
function GlobalErrorShell({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary key={location.pathname}>{children}</RouteErrorBoundary>
  );
}

/* Every route change starts the new page at the top (unless navigating
   to an in-page #anchor). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  React.useEffect(() => {
    initGoogleAds();
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <ScrollToTop />
        <BackToTop />
        <CopyAttribution />
        <PageViewTracker />
        <MetaPixelRouteTracker />
        <ChatWidget />
        <GlobalErrorShell>
          <Suspense fallback={<RouteFallback />}>
          <Routes>{renderRoutes()}</Routes>
          </Suspense>
        </GlobalErrorShell>
      </BrowserRouter>
    </AuthProvider>
  );
}
